import { Card, ComponentBoundary, useDialog } from "@fpsoftware/mvp";
import { useEffect, useState } from "react";
import {
  TbArrowsExchange,
  TbExternalLink,
  TbPlus,
  TbSettings,
} from "react-icons/tb";
import { formatMoney, formatPercent } from "shared";
import { Hint } from "@/common/Hint";
import { NoContent } from "@/common/NoContent";
import { useCompanyFees } from "@/company/useCompanyFees";
import { Button } from "@/components/Button";
import { Grid } from "@/components/grid/Grid";
import { useMortgage } from "@/mortgages/hooks";
import {
  useSuspenseMortgageOffer,
  useSuspenseMortgageOfferList,
} from "@/mortgages/mortgage-offers.hooks";
import { ManageOfferDialog } from "@/offers/ManageOfferDialog";
import styles from "./OfferList.module.css";

interface OfferListProps {
  mortgageID: number;
}

export const OfferList = (props: OfferListProps) => {
  const mortgage = useMortgage(props.mortgageID);
  const companyFees = useCompanyFees();
  const [offerId, setOfferId] = useState<number | undefined>(undefined);
  const offers = useSuspenseMortgageOfferList(mortgage.ID, mortgage.offerIDs);
  const { open } = useDialog();

  useEffect(() => {
    if (mortgage.offerIDs.length > 0 && offerId === undefined) {
      setOfferId(mortgage.offerIDs[0]);
    }

    if (offerId && !mortgage.offerIDs.includes(offerId)) {
      setOfferId(undefined);
    }
  }, [mortgage.offerIDs, offerId]);

  const handleManageOffer = () => {
    open(ManageOfferDialog, {
      mortgageID: props.mortgageID,
      onConfirm: setOfferId,
      offers: offers,
      fees: mortgage.fees.length > 0 ? mortgage.fees : companyFees,
    });
  };

  return (
    <ComponentBoundary>
      <div
        className="mb-3"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>Offers</h2>
        {mortgage.offerIDs.length > 0 && (
          <div className={styles.selector}>
            <Button
              icon={TbArrowsExchange}
              size="sm"
              onClick={() =>
                window.open(`/api/mortgage/offer/${props.mortgageID}/compare`)
              }
            >
              Overview PDF
            </Button>
            <select
              value={offerId}
              onChange={(e) => setOfferId(Number(e.target.value))}
            >
              {mortgage.offerIDs.map((offerID) => (
                <OfferOption
                  key={offerID}
                  mortgageID={mortgage.ID}
                  offerID={offerID}
                />
              ))}
            </select>
            <Button icon={TbSettings} size="sm" onClick={handleManageOffer}>
              Manage offers
            </Button>
          </div>
        )}
      </div>
      <ComponentBoundary>
        {offerId ? (
          <MortgageOfferDetail offerID={offerId} mortgageID={mortgage.ID} />
        ) : (
          <NoContent
            label="No offers created"
            sublabel="Start off by creating offers for this mortgage."
            image="/select-option.svg"
          >
            <Button icon={TbPlus} onClick={handleManageOffer}>
              Create offers
            </Button>
          </NoContent>
        )}
      </ComponentBoundary>
    </ComponentBoundary>
  );
};

function OfferOption(props: { mortgageID: number; offerID: number }) {
  const offer = useSuspenseMortgageOffer(props.mortgageID, props.offerID);

  return <option value={offer.ID}>{offer.name}</option>;
}

interface MortgageOfferDetailProps {
  offerID: number;
  mortgageID: number;
}

function MortgageOfferDetail(props: MortgageOfferDetailProps) {
  const mortgage = useMortgage(props.mortgageID);
  const offer = useSuspenseMortgageOffer(props.mortgageID, props.offerID);

  // Total Payoff, Closing Costs, Prepaids

  return (
    <Grid className="mb-6">
      <Grid.Col xs={4}>
        <Card>
          <Card.Header>Offer Fees</Card.Header>
          <Card.List>
            <Card.ListItem label="Discount points">
              {formatMoney(offer.data.fees.discountPoints)}
            </Card.ListItem>
            {offer.data.fees.list.map((fee) => (
              <Card.ListItem label={fee.name} key={fee.name}>
                {formatMoney(fee.amount)}
              </Card.ListItem>
            ))}
            <Card.ListItem label="Total">
              {formatMoney(offer.data.closingCosts)}
            </Card.ListItem>
          </Card.List>
        </Card>
      </Grid.Col>
      <Grid.Col xs={4}>
        <Card className="mb-3">
          <Card.Header>Offer details</Card.Header>
          <Card.List>
            <Card.ListItem label="Investor">
              {offer.investor || "-"}
            </Card.ListItem>
            <Card.ListItem label="Interest rate">
              {formatPercent(offer.data.interestRate)}
            </Card.ListItem>
            <Card.ListItem
              label={
                <>
                  Discount points
                  <Hint>
                    <strong>Positive:</strong> Customer pays extra
                    <br />
                    <strong>Negative:</strong> Customer receives extra
                  </Hint>
                </>
              }
            >
              {formatPercent(offer.data.discountPoints)}
            </Card.ListItem>
            <Card.ListItem label="Discount (after comp.)">
              {formatPercent(offer.data.customerDiscountPercentage)}
            </Card.ListItem>
            <Card.ListItem label="Compensation">
              {formatMoney(offer.data.compensation)}
            </Card.ListItem>
            <Card.ListItem label="Amount due at closing">
              {formatMoney(offer.data.amountDue)}
            </Card.ListItem>
            <Card.ListItem label="Mortgage insurance">
              {formatMoney(offer.data.mortgageInsurance.monthly)}/mo
              <br />
              <small>
                {formatPercent(offer.data.mortgageInsurance.percentage)}
              </small>
            </Card.ListItem>
            <Card.ListItem label="Loan amount">
              {formatMoney(offer.data.balance)}
            </Card.ListItem>
          </Card.List>
        </Card>
        <Card>
          <Card.Header>Prepays & Escrows</Card.Header>
          <Card.List>
            <Card.ListItem label="Property tax">
              {formatMoney(mortgage.remortgage.prepays.propertyTax.amount)}
              <br />
              <small>
                {mortgage.remortgage.prepays.propertyTax.months} months
              </small>
            </Card.ListItem>
            <Card.ListItem label="Property insurance">
              {formatMoney(
                mortgage.remortgage.prepays.propertyInsurance.amount,
              )}
              <br />
              <small>
                {mortgage.remortgage.prepays.propertyInsurance.months} months
              </small>
            </Card.ListItem>
            <Card.ListItem label="Prepaid interest">
              {formatMoney(offer.data.prepaidInterest.due)}
              <br />
              <small>{offer.data.prepaidInterest.days} days</small>
            </Card.ListItem>
          </Card.List>
        </Card>
      </Grid.Col>
      <Grid.Col xs={4}>
        <Card className="mb-3">
          <Card.Header>Summary</Card.Header>
          <Card.List>
            <Card.ListItem label="Total Payoff, Closing Costs, Prepaids">
              {formatMoney(offer.data.payoff)}
            </Card.ListItem>
            <Card.ListItem label="New loan amount">
              {formatMoney(offer.data.balance)}
            </Card.ListItem>
            <Card.ListItem label="Amount due at closing">
              {formatMoney(offer.data.amountDue)}
            </Card.ListItem>
          </Card.List>
        </Card>
        <Card className="mb-3">
          <Card.Header>Output PDFs</Card.Header>
          <Card.List>
            <Card.ListItem
              label={
                <div>
                  Detail PDF
                  <br />
                  <small>
                    Compare current mortgage and "{offer.name}" offer
                  </small>
                </div>
              }
            >
              <Button
                icon={TbExternalLink}
                onClick={() =>
                  window.open(`/api/mortgage/offer/${props.offerID}/pdf`)
                }
              >
                Details
              </Button>
            </Card.ListItem>
          </Card.List>
        </Card>
      </Grid.Col>
    </Grid>
  );
}
